// ─── The site icon, from a picture the owner chooses ──────────────────────────
// The site dashboard lets the owner put a picture of their own where the icon
// the model drew was: the browser tab, the site's card, favicon.svg in every
// download. The server has no image library (a native one is what breaks a
// Hostinger install), so the crop and the shrink happen here, on a canvas, and
// only the finished 128px picture is sent (PUT /api/websites/:id/icon, which
// frames it in the SVG every icon consumer reads: services/logoIcon.js).
//
// A crop is in units of the frame, which is 1×1: `scale` is frame widths per
// picture pixel and `x`/`y` place the picture's top-left corner. The same crop
// therefore draws the same icon in the editor's frame at any size, in the
// small previews beside it, and on the 128px canvas.

import limits from '../../builderslab/shared/limits.json';
import { authFetch } from '@/lib/authFetch';

/** The icon's size, as the browser draws it. Matches the logo path (promptInput.js). */
export const ICON_PX = 128;
/** Only the 128px result is sent, so a large original costs nothing but decoding. */
export const MAX_PICTURE_BYTES = 20 * 1024 * 1024;
/** The server refuses a longer data URL: icon_svg is a TEXT column. */
export const MAX_ICON_CHARS = limits.siteIconChars;
/** How far in the zoom goes, past the point where the picture fills the frame. */
const MAX_ZOOM = 4;

/** Marks an icon as the owner's own picture (services/logoIcon.js LOGO_MARK). */
export function isOwnIcon(svg) {
  return typeof svg === 'string' && svg.includes('data-bl-logo="1"');
}

/** The zoom at which the whole picture fits inside the frame. */
export function fitScale(w, h) { return 1 / Math.max(w, h); }
/** The zoom at which the picture just covers the frame. */
export function coverScale(w, h) { return 1 / Math.min(w, h); }
function maxScale(w, h) { return coverScale(w, h) * MAX_ZOOM; }
function clampScale(img, scale) {
  return Math.min(maxScale(img.w, img.h), Math.max(fitScale(img.w, img.h), scale));
}

/** One axis: a side larger than the frame stays over it; a smaller one is centred. */
function clampAxis(pos, size) {
  if (size <= 1) return (1 - size) / 2;
  return Math.min(0, Math.max(1 - size, pos));
}

/**
 * A crop made valid: the zoom inside its range, and the picture covering the
 * frame wherever it can (centred along a side where it is smaller).
 * @param {{w: number, h: number}} img  the picture's natural size
 * @param {{scale: number, x: number, y: number}} crop
 */
export function clampCrop(img, crop) {
  const scale = clampScale(img, crop.scale);
  return { scale, x: clampAxis(crop.x, img.w * scale), y: clampAxis(crop.y, img.h * scale) };
}

/** Where every picture starts: filling the frame, centred. */
export function initialCrop(img) {
  const scale = coverScale(img.w, img.h);
  return clampCrop(img, { scale, x: (1 - img.w * scale) / 2, y: (1 - img.h * scale) / 2 });
}

/** Zoom to `scale`, keeping whatever is under the frame's centre where it is. */
export function zoomCrop(img, crop, scale) {
  const next = clampScale(img, scale);
  const cx = (0.5 - crop.x) / crop.scale;
  const cy = (0.5 - crop.y) / crop.scale;
  return clampCrop(img, { scale: next, x: 0.5 - cx * next, y: 0.5 - cy * next });
}

/** Move the picture by (dx, dy) frame widths. */
export function moveCrop(img, crop, dx, dy) {
  return clampCrop(img, { ...crop, x: crop.x + dx, y: crop.y + dy });
}

/** The zoom range for a picture, for the slider: [whole picture, close up]. */
export function zoomRange(img) {
  return { min: fitScale(img.w, img.h), cover: coverScale(img.w, img.h), max: maxScale(img.w, img.h) };
}

/**
 * CSS that places the picture in any square box with the crop (percentages).
 * @returns {import('react').CSSProperties}
 */
export function cropStyle(img, crop) {
  return {
    position: 'absolute',
    left: `${crop.x * 100}%`,
    top: `${crop.y * 100}%`,
    width: `${img.w * crop.scale * 100}%`,
    height: `${img.h * crop.scale * 100}%`,
    maxWidth: 'none',
  };
}

function canvasOf(px) {
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  return canvas;
}

/**
 * The crop drawn on a square canvas `px` wide, or null where there is no
 * canvas. A large photo is halved step by step first: one drawImage from
 * 4000px to 128px drops most of the pixels it should have averaged, and the
 * icon comes out jagged.
 */
export function drawCrop(source, img, crop, px = ICON_PX) {
  const canvas = canvasOf(px);
  const ctx = canvas.getContext?.('2d');
  if (!ctx) return null;
  let src = source;
  let sw = img.w;
  let sh = img.h;
  const dw = img.w * crop.scale * px;
  while (sw > dw * 2 && sw > 2) {
    const half = document.createElement('canvas');
    half.width = Math.max(1, Math.round(sw / 2));
    half.height = Math.max(1, Math.round(sh / 2));
    const hctx = half.getContext('2d');
    if (!hctx) break;
    hctx.imageSmoothingQuality = 'high';
    hctx.drawImage(src, 0, 0, half.width, half.height);
    src = half;
    sw = half.width;
    sh = half.height;
  }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, crop.x * px, crop.y * px, dw, img.h * crop.scale * px);
  return canvas;
}

/** Whether any pixel on the canvas is see-through. */
export function hasTransparency(canvas) {
  const { data } = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
  return false;
}

/**
 * The canvas as the smallest data URL the server will take, or '' when none
 * fits. JPEG unless the icon is see-through anywhere: a photo is a few KB as
 * JPEG and tens as PNG, and JPEG would paint a logo's clear background black.
 */
export function encodeIcon(canvas) {
  if (!hasTransparency(canvas)) {
    for (const quality of [0.9, 0.8, 0.7, 0.6]) {
      const url = canvas.toDataURL('image/jpeg', quality);
      if (url.startsWith('data:image/jpeg;base64,') && url.length <= MAX_ICON_CHARS) return url;
    }
    return '';
  }
  for (const px of [ICON_PX, 96, 64]) {
    let out = canvas;
    if (px !== canvas.width) {
      out = canvasOf(px);
      const ctx = out.getContext?.('2d');
      if (!ctx) return '';
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(canvas, 0, 0, px, px);
    }
    const url = out.toDataURL('image/png');
    if (url.startsWith('data:image/png;base64,') && url.length <= MAX_ICON_CHARS) return url;
  }
  return '';
}

/**
 * A picture file, opened: an object URL to show it, the decoded image to draw
 * it, and its natural size. The caller revokes `url` when done.
 * @param {File} file
 * @throws {Error} with a message meant for the owner
 */
export async function openPicture(file) {
  if (!file) throw new Error('Choose a picture.');
  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('That file is not a picture. Choose a PNG, JPG or WebP.');
  }
  if (file.size > MAX_PICTURE_BYTES) {
    throw new Error('That picture is over 20 MB. Choose a smaller one.');
  }
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const el = new Image();
      const timer = setTimeout(() => reject(new Error('timeout')), 15000);
      el.onload = () => { clearTimeout(timer); resolve(el); };
      el.onerror = () => { clearTimeout(timer); reject(new Error('undecodable')); };
      el.src = url;
    });
    // An SVG with only a viewBox reports no size of its own; treat it as square.
    return { url, image, w: image.naturalWidth || ICON_PX, h: image.naturalHeight || ICON_PX };
  } catch {
    URL.revokeObjectURL?.(url);
    throw new Error('That picture could not be opened. Try a PNG or JPG.');
  }
}

/** Save a finished icon picture (a data URL from encodeIcon). */
export function saveSiteIcon(websiteId, image) {
  return authFetch(`/api/websites/${encodeURIComponent(websiteId)}/icon`, { method: 'PUT', body: JSON.stringify({ image }) });
}

/** Put back the icon the site had before the owner's picture. */
export function restoreSiteIcon(websiteId) {
  return authFetch(`/api/websites/${encodeURIComponent(websiteId)}/icon`, { method: 'DELETE' });
}
